import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import {
    MonitorCog , SquareActivity , Tickets , Menu, X, Home, Package, FileText, Calendar, Users, BarChart3, Settings,
    Bell, Search, ChevronDown, ArrowRight, TrendingUp, TrendingDown,
    Building, Hammer, Truck, Star, AlertCircle, CheckCircle, Clock,
    DollarSign, Activity, Globe, Zap, Target, Award, Shield, LogOut,
    ChevronRight, Filter, Download, RefreshCw, MoreVertical, Plus, User,
    Edit, Trash2, Eye, Upload, UserCheck, UserX, Calendar as CalendarIcon,
    PieChart, BarChart, Percent, Timer, MapPin, Coffee, PlayCircle, PauseCircle,
    ArrowLeft, ChevronUp, Cable, UserCog, Calculator, Construction, Beaker,Receipt , Briefcase,
    Leaf, ShieldCheck, Wrench
} from 'lucide-react';
import '../Styles/products.css';
import { useNavigate } from 'react-router-dom';
import MepIcon from '../assets/MEP_Solutions.png';
import architecturalIcon from '../assets/architectural_icon.png';
import civilIcon from '../assets/civil-icon.png';
import constructionIcon from '../assets/construction-icon.png';
import powerToolsIcon from '../assets/power-tools_icon.png';
import sustainableIcon from '../assets/sustainable-icon.png';
import hygienicIcon from '../assets/hygienic-icon.png';
import '../Styles/dashboard.css';

const IntegratedTeamManagement = () => {
    // State Management
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenuItem, setActiveMenuItem] = useState('team');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    // Data states
    const [dashboardData, setDashboardData] = useState({
        totalEmployees: 0,
        totalHoursThisWeek: 0,
        totalHoursThisMonth: 0,
        avgHoursPerEmployee: 0,
        attendanceRate: 0
    });

    const [weeklyData, setWeeklyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [employeeDetails, setEmployeeDetails] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('current_week');

    // Filter states
    const [viewMode, setViewMode] = useState('dashboard'); // dashboard, weekly, monthly, employee-detail
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

    // Notifications
    const [notifications, setNotifications] = useState([]);

    const navigate = useNavigate();



    // Helper functions
    function getCurrentWeek() {
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 1));
        return startOfWeek.toISOString().split('T')[0];
    }

    function getCurrentMonth() {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }

    // Time update effect
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Load user data
    useEffect(() => {
        loadCurrentUser();
        loadDashboardData();
    }, []);

    // Load data based on view mode
    useEffect(() => {
        switch(viewMode) {
            case 'weekly':
                loadWeeklyData();
                break;
            case 'monthly':
                loadMonthlyData();
                break;
            case 'employee-detail':
                if (selectedEmployee) loadEmployeeDetails();
                break;
            default:
                loadDashboardData();
        }
    }, [viewMode, selectedWeek, selectedMonth, selectedEmployee]);

    // API Calls
    const loadCurrentUser = async () => {
        try {
            const response = await ApiService.getCurrentUser();
            if (response.success) {
                setUser(response.user);
            }
        } catch (error) {
            console.error('Failed to load user:', error);
            navigate('/login');
        }
    };

    const loadDashboardData = async () => {
        try {
            setIsLoading(true);
            const response = await ApiService.getTeamDashboard();

            if (response.success) {
                setDashboardData(response.dashboard);
            } else {
                showNotification('Erreur lors du chargement du dashboard', 'error');
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
            showNotification('Erreur lors du chargement du dashboard', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadWeeklyData = async () => {
        try {
            setIsLoading(true);
            const response = await ApiService.getWeeklyAttendance({
                week: selectedWeek
            });

            if (response.success) {
                setWeeklyData(response.weekly_data || []);
            } else {
                showNotification('Erreur lors du chargement des données hebdomadaires', 'error');
            }
        } catch (error) {
            console.error('Failed to load weekly data:', error);
            showNotification('Erreur lors du chargement des données hebdomadaires', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadMonthlyData = async () => {
        try {
            setIsLoading(true);
            const response = await ApiService.getMonthlyAttendance({
                month: selectedMonth
            });

            if (response.success) {
                setMonthlyData(response.monthly_data || []);
            } else {
                showNotification('Erreur lors du chargement des données mensuelles', 'error');
            }
        } catch (error) {
            console.error('Failed to load monthly data:', error);
            showNotification('Erreur lors du chargement des données mensuelles', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadEmployeeDetails = async () => {
        try {
            setIsLoading(true);
            const response = await ApiService.getEmployeeAttendanceDetails({
                employee_id: selectedEmployee.id,
                period: selectedPeriod
            });

            if (response.success) {
                setEmployeeDetails(response.employee_details || []);
            } else {
                showNotification('Erreur lors du chargement des détails employé', 'error');
            }
        } catch (error) {
            console.error('Failed to load employee details:', error);
            showNotification('Erreur lors du chargement des détails employé', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const syncWithZKDevice = async () => {
        setIsSyncing(true);
        showNotification('Synchronisation avec le dispositif ZK en cours...', 'info', 8000);

        try {
            const response = await ApiService.syncZKDeviceData({
                ip: '192.168.1.60',
                port: 4370,
                auto_process: true
            });

            if (response.success) {
                showNotification(
                    `Synchronisation réussie! ${response.records_processed || 0} enregistrements traités.`,
                    'success'
                );

                // Reload current view data
                switch(viewMode) {
                    case 'weekly':
                        await loadWeeklyData();
                        break;
                    case 'monthly':
                        await loadMonthlyData();
                        break;
                    default:
                        await loadDashboardData();
                }
            } else {
                showNotification(`Erreur de synchronisation: ${response.message}`, 'error');
            }
        } catch (error) {
            console.error('Failed to sync ZK data:', error);
            showNotification('Erreur lors de la synchronisation avec le dispositif ZK', 'error');
        } finally {
            setIsSyncing(false);
        }
    };

    const processCurrentPeriod = async (type = 'week') => {
        try {
            setIsLoading(true);
            showNotification(`Traitement des données ${type === 'week' ? 'hebdomadaires' : 'mensuelles'} en cours...`, 'info');

            const response = await ApiService.processAttendanceData({
                type: type,
                auto_calculate: true
            });

            if (response.success) {
                showNotification(`Données ${type === 'week' ? 'hebdomadaires' : 'mensuelles'} traitées avec succès!`, 'success');

                // Reload data
                if (type === 'week') {
                    await loadWeeklyData();
                } else {
                    await loadMonthlyData();
                }
                await loadDashboardData();
            } else {
                showNotification('Erreur lors du traitement des données', 'error');
            }
        } catch (error) {
            console.error('Failed to process period data:', error);
            showNotification('Erreur lors du traitement des données', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const exportReport = async (type, data) => {
        try {
            setIsLoading(true);
            showNotification('Génération du rapport en cours...', 'info');

            const response = await ApiService.exportAttendanceReport({
                type: type,
                data: data,
                format: 'excel'
            });

            if (response.success) {
                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', response.filename || `attendance_report_${type}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                showNotification('Rapport téléchargé avec succès!', 'success');
            } else {
                showNotification('Erreur lors de la génération du rapport', 'error');
            }
        } catch (error) {
            console.error('Failed to export report:', error);
            showNotification('Erreur lors de la génération du rapport', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Notification system
    const showNotification = (message, type = 'success', duration = 4000) => {
        const id = Date.now() + Math.random();
        const notification = { id, message, type, duration };
        setNotifications(prev => [...prev, notification]);
        setTimeout(() => removeNotification(id), duration);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };

    // Helper functions for display
    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString.substring(0, 5);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'present': { text: 'Présent', color: '#059669' },
            'absent': { text: 'Absent', color: '#dc2626' },
            'incomplete': { text: 'Incomplet', color: '#f59e0b' },
            'late': { text: 'Retard', color: '#f59e0b' }
        };
        return statusMap[status] || statusMap.absent;
    };

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    // Menu items
    const menuItems = [
        { id: 'barcoding', label: 'Barcoding Database', icon: FileText, path: '/barcoding' },
        { id: 'mep-solutions', label: 'MEP Solutions', icon: MepIcon, isImage: true, path: '/mep_solutions' },
        { id: 'architectural-solutions', label: 'Architectural Solutions', icon: architecturalIcon, isImage: true, path: '/architectural_solutions' },
        { id: 'civil-solutions', label: 'Civil Solutions', icon: civilIcon, isImage: true, path: '/civil_solutions' },
        { id: 'construction-chemicals', label: 'Construction Chemicals', icon: constructionIcon, isImage: true, path: '/construction_chemicals' },
        { id: 'power-tools', label: 'Power & Industrial Tools', icon: powerToolsIcon, isImage: true, path: '/power_tools' },
        { id: 'sustainable-solutions', label: 'Sustainable Solutions', icon: sustainableIcon, isImage: true, path: '/sustainable_solutions' },
        { id: 'hygienic-solutions', label: 'Hygienic Solutions', icon: hygienicIcon, isImage: true, path: '/hygienic_solutions' },
        { id: 'conversions', label: 'Engineering Conversions', icon: Calculator, path: '/conversion' },
        { id: 'analytics', label: 'Amazon Products', icon: BarChart3, path: '/amazon_products' },
        { id: 'team', label: 'Team Management', icon: Users, path: '/team_management' },
        { id: 'user-management', label: 'User Management', icon: UserCog, path: '/users_management' },
    ];

    const filteredMenuItems = menuItems.filter(item => {
        if (item.id === 'team' && user?.role === 'user') return false;
        if (item.id === 'settings' && user?.role !== 'admin') return false;
        return true;
    });

    const handleLogout = async () => {
        if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter?')) {
            ApiService.clearUserData();
            navigate('/login');
        }
    };

    // Notification Component
    const NotificationItem = ({ notification, onRemove }) => {
        const getNotificationStyle = (type) => {
            const baseStyle = {
                padding: '16px 20px',
                borderRadius: '12px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                minWidth: '350px',
                maxWidth: '500px',
                position: 'relative',
                animation: 'slideInRight 0.3s ease-out',
                cursor: 'pointer'
            };

            const styles = {
                success: { ...baseStyle, backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' },
                error: { ...baseStyle, backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' },
                warning: { ...baseStyle, backgroundColor: '#fffbeb', borderColor: '#fed7aa', color: '#d97706' },
                info: { ...baseStyle, backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }
            };

            return styles[type] || styles.info;
        };

        const getIcon = (type) => {
            const iconStyle = { width: '20px', height: '20px' };
            switch (type) {
                case 'success': return <CheckCircle style={iconStyle} />;
                case 'error': return <AlertCircle style={iconStyle} />;
                case 'warning': return <AlertCircle style={iconStyle} />;
                default: return <Bell style={iconStyle} />;
            }
        };

        return (
            <div
                style={getNotificationStyle(notification.type)}
                onClick={() => onRemove(notification.id)}
            >
                {getIcon(notification.type)}
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                        {notification.message}
                    </p>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(notification.id);
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        opacity: 0.7
                    }}
                >
                    <X style={{ width: '16px', height: '16px' }} />
                </button>
            </div>
        );
    };

    if (!user) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column'
            }}>
                <RefreshCw style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '16px' }}>Chargement...</p>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div
                        className="sidebar-logo-container"
                        onClick={() => navigate('/dashboard')}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderRadius: '8px',
                            padding: '8px'
                        }}
                        title="Retour au Dashboard"
                    >
                        <div className="sidebar-logo">
                            <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                        </div>
                        <div className="sidebar-brand">
                            <h1 className="sidebar-title">UNITECH IKK</h1>
                            <p className="sidebar-subtitle">Enterprise Dashboard</p>
                        </div>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {filteredMenuItems.map((item) => (
                        <div
                            key={item.id}
                            className={`nav-item ${activeMenuItem === item.id ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMenuItem(item.id);
                                navigate(item.path);
                            }}
                        >
                            {item.isImage ? (
                                <img
                                    src={item.icon}
                                    alt={item.label}
                                    className="nav-icon-image"
                                    style={{ width: '20px', height: '20px', display: 'block' }}
                                />
                            ) : (
                                <item.icon style={{ width: '20px', height: '20px', display: 'block' }} />
                            )}
                            <span className="nav-text">{item.label}</span>
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">{getUserInitials()}</div>
                        <div className="user-info">
                            <p className="user-name">{getDisplayName()}</p>
                            <p className="user-role">{user?.role || 'Utilisateur'}</p>
                        </div>
                        <button
                            className="logout-button"
                            onClick={handleLogout}
                            title="Se déconnecter"
                        >
                            <LogOut style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Header */}
                <header className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                            >
                                {sidebarOpen ?
                                    <X style={{ width: '20px', height: '20px' }} /> :
                                    <Menu style={{ width: '20px', height: '20px' }} />
                                }
                            </button>

                            <nav className="breadcrumb">
                                <div className="breadcrumb-item">
                                    <Home style={{ width: '16px', height: '16px' }} />
                                    <span>Dashboard</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <Users style={{ width: '16px', height: '16px' }} />
                                    <span>Team Management</span>
                                </div>
                                {viewMode !== 'dashboard' && (
                                    <>
                                        <ChevronRight style={{ width: '14px', height: '14px' }} />
                                        <div className="breadcrumb-item">
                                            <span>{viewMode === 'weekly' ? 'Vue Hebdomadaire' : viewMode === 'monthly' ? 'Vue Mensuelle' : 'Détails Employé'}</span>
                                        </div>
                                    </>
                                )}
                            </nav>
                        </div>

                        <div className="header-right">
                            <div className="search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Rechercher employé..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                            </div>

                            <div className="header-actions">
                                <button
                                    className="header-button"
                                    onClick={syncWithZKDevice}
                                    disabled={isSyncing || isLoading}
                                    title="Synchroniser avec ZK Device"
                                    style={{
                                        backgroundColor: isSyncing ? '#f59e0b' : '#059669',
                                        color: 'white'
                                    }}
                                >
                                    <Cable style={{
                                        width: '18px',
                                        height: '18px',
                                        animation: isSyncing ? 'spin 2s linear infinite' : 'none'
                                    }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={loadDashboardData}
                                    disabled={isLoading}
                                    title="Actualiser"
                                >
                                    <RefreshCw style={{
                                        width: '18px',
                                        height: '18px',
                                        animation: isLoading ? 'spin 1s linear infinite' : 'none'
                                    }} />
                                </button>

                                <button
                                    className="header-button"
                                    title="Notifications"
                                >
                                    <Bell style={{ width: '18px', height: '18px' }} />
                                    {notifications.length > 0 && (
                                        <span className="notification-badge">{notifications.length}</span>
                                    )}
                                </button>

                                <div className="time-display">
                                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content Area */}
                <div className="products-content">
                    {viewMode === 'dashboard' ? (
                        // DASHBOARD VIEW
                        <>
                            <div className="page-header">
                                <h1 className="page-title">Team Management Dashboard</h1>
                                <p className="page-subtitle">
                                    Gestion des présences et heures de travail - Synchronisé avec ZK Device
                                </p>

                                <div className="page-actions">
                                    <button
                                        className="action-button secondary"
                                        onClick={() => setViewMode('weekly')}
                                    >
                                        <Calendar style={{ width: '16px', height: '16px' }} />
                                        Vue Hebdomadaire
                                    </button>
                                    <button
                                        className="action-button secondary"
                                        onClick={() => setViewMode('monthly')}
                                    >
                                        <CalendarIcon style={{ width: '16px', height: '16px' }} />
                                        Vue Mensuelle
                                    </button>
                                    <button
                                        className="action-button"
                                        onClick={syncWithZKDevice}
                                        disabled={isSyncing}
                                    >
                                        <Cable style={{ width: '16px', height: '16px' }} />
                                        {isSyncing ? 'Synchronisation...' : 'Sync ZK Data'}
                                    </button>
                                </div>
                            </div>

                            {/* Dashboard Stats */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                                    padding: '24px', borderRadius: '12px', color: 'white'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>Total Employés</h3>
                                            <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>{dashboardData.totalEmployees}</p>
                                        </div>
                                        <Users style={{ width: '40px', height: '40px', opacity: 0.8 }} />
                                    </div>
                                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                                        Actifs cette semaine
                                    </div>
                                </div>

                                <div style={{
                                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                    padding: '24px', borderRadius: '12px', color: 'white'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>Heures Cette Semaine</h3>
                                            <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>{dashboardData.totalHoursThisWeek}h</p>
                                        </div>
                                        <Timer style={{ width: '40px', height: '40px', opacity: 0.8 }} />
                                    </div>
                                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                                        Semaine en cours
                                    </div>
                                </div>

                                <div style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                                    padding: '24px', borderRadius: '12px', color: 'white'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>Heures Ce Mois</h3>
                                            <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>{dashboardData.totalHoursThisMonth}h</p>
                                        </div>
                                        <BarChart style={{ width: '40px', height: '40px', opacity: 0.8 }} />
                                    </div>
                                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                                        Mois en cours
                                    </div>
                                </div>

                                <div style={{
                                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                    padding: '24px', borderRadius: '12px', color: 'white'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>Taux de Présence</h3>
                                            <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>{dashboardData.attendanceRate}%</p>
                                        </div>
                                        <Percent style={{ width: '40px', height: '40px', opacity: 0.8 }} />
                                    </div>
                                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                                        Moyenne équipe
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px',
                                marginBottom: '30px'
                            }}>
                                <button
                                    className="quick-action-card"
                                    onClick={() => processCurrentPeriod('week')}
                                    disabled={isLoading}
                                    style={{
                                        padding: '20px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        background: 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Clock style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: '#059669' }} />
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Traiter Semaine</h4>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Calculer heures semaine courante</p>
                                </button>

                                <button
                                    className="quick-action-card"
                                    onClick={() => processCurrentPeriod('month')}
                                    disabled={isLoading}
                                    style={{
                                        padding: '20px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        background: 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <CalendarIcon style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: '#f59e0b' }} />
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Traiter Mois</h4>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Calculer heures mois courant</p>
                                </button>

                                <button
                                    className="quick-action-card"
                                    onClick={() => exportReport('dashboard', dashboardData)}
                                    disabled={isLoading}
                                    style={{
                                        padding: '20px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        background: 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Download style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: '#1e40af' }} />
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Exporter Rapport</h4>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Télécharger rapport complet</p>
                                </button>

                                <button
                                    className="quick-action-card"
                                    onClick={syncWithZKDevice}
                                    disabled={isSyncing}
                                    style={{
                                        padding: '20px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        background: 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Cable style={{
                                        width: '24px',
                                        height: '24px',
                                        margin: '0 auto 8px',
                                        color: '#dc2626',
                                        animation: isSyncing ? 'spin 2s linear infinite' : 'none'
                                    }} />
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>
                                        {isSyncing ? 'Synchronisation...' : 'Sync ZK Device'}
                                    </h4>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                                        {isSyncing ? 'En cours...' : 'Récupérer nouvelles données'}
                                    </p>
                                </button>
                            </div>

                            {/* Recent Activity */}
                            <div className="products-table-section">
                                <div className="table-header">
                                    <h2 className="table-title">Activité Récente</h2>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => setViewMode('weekly')}
                                        >
                                            Voir Hebdomadaire
                                        </button>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => setViewMode('monthly')}
                                        >
                                            Voir Mensuel
                                        </button>
                                    </div>
                                </div>

                                <div style={{
                                    padding: '40px',
                                    textAlign: 'center',
                                    color: '#64748b',
                                    border: '2px dashed #e2e8f0',
                                    borderRadius: '12px'
                                }}>
                                    <Users style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
                                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                        Choisissez une vue pour voir les détails
                                    </p>
                                    <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                                        Sélectionnez Vue Hebdomadaire ou Vue Mensuelle pour voir les heures détaillées
                                    </p>
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                        <button
                                            className="action-button"
                                            onClick={() => setViewMode('weekly')}
                                        >
                                            <Calendar style={{ width: '16px', height: '16px' }} />
                                            Vue Hebdomadaire
                                        </button>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => setViewMode('monthly')}
                                        >
                                            <CalendarIcon style={{ width: '16px', height: '16px' }} />
                                            Vue Mensuelle
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : viewMode === 'weekly' ? (
                        // WEEKLY VIEW - Continue with complete implementation
                        <>
                            <div style={{ marginBottom: '20px' }}>
                                <button
                                    onClick={() => setViewMode('dashboard')}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #d1d5db',
                                        background: 'white',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <ArrowLeft style={{ width: '16px', height: '16px' }} />
                                    Retour au Dashboard
                                </button>

                                <div className="page-header">
                                    <h1 className="page-title">Vue Hebdomadaire</h1>
                                    <p className="page-subtitle">
                                        Heures de travail par semaine - Données synchronisées avec ZK Device
                                    </p>

                                    <div className="page-actions">
                                        <input
                                            type="week"
                                            value={selectedWeek}
                                            onChange={(e) => setSelectedWeek(e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        <button
                                            className="action-button secondary"
                                            onClick={() => processCurrentPeriod('week')}
                                            disabled={isLoading}
                                        >
                                            <Clock style={{ width: '16px', height: '16px' }} />
                                            Traiter Semaine
                                        </button>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => exportReport('weekly', weeklyData)}
                                        >
                                            <Download style={{ width: '16px', height: '16px' }} />
                                            Exporter
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Weekly Data Table */}
                            <div className="products-table-section">
                                <div className="table-header">
                                    <h2 className="table-title">Données Hebdomadaires</h2>
                                    <span className="table-stats">
                                        {weeklyData.length} employé{weeklyData.length > 1 ? 's' : ''}
                                    </span>
                                </div>

                                {isLoading ? (
                                    <div style={{ padding: '40px', textAlign: 'center' }}>
                                        <RefreshCw style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                                        <p style={{ marginTop: '16px' }}>Chargement des données hebdomadaires...</p>
                                    </div>
                                ) : (
                                    <table className="products-table">
                                        <thead className="table-head">
                                        <tr>
                                            <th>Employé</th>
                                            <th>Lundi</th>
                                            <th>Mardi</th>
                                            <th>Mercredi</th>
                                            <th>Jeudi</th>
                                            <th>Vendredi</th>
                                            <th>Total Semaine</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody className="table-body">
                                        {weeklyData.filter(employee =>
                                            employee.name.toLowerCase().includes(searchTerm.toLowerCase())
                                        ).map((employee) => (
                                            <tr
                                                key={employee.id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setSelectedEmployee(employee);
                                                    setViewMode('employee-detail');
                                                }}
                                            >
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            width: '40px', height: '40px', backgroundColor: '#1e40af',
                                                            borderRadius: '50%', display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '600'
                                                        }}>
                                                            {employee.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                        </div>
                                                        <div>
                                                            <div className="product-name" style={{ fontWeight: '600' }}>
                                                                {employee.name}
                                                            </div>
                                                            <div className="product-sku" style={{ fontSize: '12px', color: '#6b7280' }}>
                                                                {employee.department}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.monday_hours > 0 ? '#1e40af' : '#6b7280'
                                                        }}>
                                                            {employee.monday_hours ? `${employee.monday_hours.toFixed(1)}h` : '-'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.tuesday_hours > 0 ? '#1e40af' : '#6b7280'
                                                        }}>
                                                            {employee.tuesday_hours ? `${employee.tuesday_hours.toFixed(1)}h` : '-'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.wednesday_hours > 0 ? '#1e40af' : '#6b7280'
                                                        }}>
                                                            {employee.wednesday_hours ? `${employee.wednesday_hours.toFixed(1)}h` : '-'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.thursday_hours > 0 ? '#1e40af' : '#6b7280'
                                                        }}>
                                                            {employee.thursday_hours ? `${employee.thursday_hours.toFixed(1)}h` : '-'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.friday_hours > 0 ? '#1e40af' : '#6b7280'
                                                        }}>
                                                            {employee.friday_hours ? `${employee.friday_hours.toFixed(1)}h` : '-'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                            color: '#059669'
                                                        }}>
                                                            {employee.total_hours ? `${employee.total_hours.toFixed(1)}h` : '0h'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span
                                                            className="status-badge"
                                                            style={{
                                                                backgroundColor: employee.total_hours >= 40 ? '#059669' + '20' : employee.total_hours >= 20 ? '#f59e0b' + '20' : '#dc2626' + '20',
                                                                color: employee.total_hours >= 40 ? '#059669' : employee.total_hours >= 20 ? '#f59e0b' : '#dc2626',
                                                                border: `1px solid ${employee.total_hours >= 40 ? '#059669' : employee.total_hours >= 20 ? '#f59e0b' : '#dc2626'}40`
                                                            }}
                                                        >
                                                            {employee.total_hours >= 40 ? 'Complet' : employee.total_hours >= 20 ? 'Partiel' : 'Faible'}
                                                        </span>
                                                </td>
                                                <td>
                                                    <div className="actions-cell">
                                                        <button
                                                            className="action-icon"
                                                            title="Voir détails"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployee(employee);
                                                                setViewMode('employee-detail');
                                                            }}
                                                        >
                                                            <Eye style={{ width: '16px', height: '16px' }} />
                                                        </button>
                                                        <button className="action-icon" title="Modifier">
                                                            <Edit style={{ width: '16px', height: '16px' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}

                                {!isLoading && weeklyData.length === 0 && (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#64748b'
                                    }}>
                                        <Calendar style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
                                        <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            Aucune donnée hebdomadaire trouvée
                                        </p>
                                        <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                                            Synchronisez avec le dispositif ZK pour récupérer les données.
                                        </p>
                                        <button
                                            className="action-button"
                                            onClick={syncWithZKDevice}
                                            disabled={isSyncing}
                                        >
                                            <Cable style={{ width: '16px', height: '16px' }} />
                                            Synchroniser maintenant
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : viewMode === 'monthly' ? (
                        // MONTHLY VIEW - Complete implementation
                        <>
                            <div style={{ marginBottom: '20px' }}>
                                <button
                                    onClick={() => setViewMode('dashboard')}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #d1d5db',
                                        background: 'white',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <ArrowLeft style={{ width: '16px', height: '16px' }} />
                                    Retour au Dashboard
                                </button>

                                <div className="page-header">
                                    <h1 className="page-title">Vue Mensuelle</h1>
                                    <p className="page-subtitle">
                                        Heures de travail par mois - Données synchronisées avec ZK Device
                                    </p>

                                    <div className="page-actions">
                                        <input
                                            type="month"
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        <button
                                            className="action-button secondary"
                                            onClick={() => processCurrentPeriod('month')}
                                            disabled={isLoading}
                                        >
                                            <CalendarIcon style={{ width: '16px', height: '16px' }} />
                                            Traiter Mois
                                        </button>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => exportReport('monthly', monthlyData)}
                                        >
                                            <Download style={{ width: '16px', height: '16px' }} />
                                            Exporter
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Monthly Data Table */}
                            <div className="products-table-section">
                                <div className="table-header">
                                    <h2 className="table-title">Données Mensuelles</h2>
                                    <span className="table-stats">
                                        {monthlyData.length} employé{monthlyData.length > 1 ? 's' : ''}
                                    </span>
                                </div>

                                {isLoading ? (
                                    <div style={{ padding: '40px', textAlign: 'center' }}>
                                        <RefreshCw style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                                        <p style={{ marginTop: '16px' }}>Chargement des données mensuelles...</p>
                                    </div>
                                ) : (
                                    <table className="products-table">
                                        <thead className="table-head">
                                        <tr>
                                            <th>Employé</th>
                                            <th>Heures Totales</th>
                                            <th>Jours Travaillés</th>
                                            <th>Moyenne/Jour</th>
                                            <th>Heures Sup</th>
                                            <th>Taux Présence</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody className="table-body">
                                        {monthlyData.filter(employee =>
                                            employee.name.toLowerCase().includes(searchTerm.toLowerCase())
                                        ).map((employee) => (
                                            <tr
                                                key={employee.id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setSelectedEmployee(employee);
                                                    setViewMode('employee-detail');
                                                }}
                                            >
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            width: '40px', height: '40px', backgroundColor: '#1e40af',
                                                            borderRadius: '50%', display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '600'
                                                        }}>
                                                            {employee.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                        </div>
                                                        <div>
                                                            <div className="product-name" style={{ fontWeight: '600' }}>
                                                                {employee.name}
                                                            </div>
                                                            <div className="product-sku" style={{ fontSize: '12px', color: '#6b7280' }}>
                                                                {employee.department}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af' }}>
                                                            {employee.total_hours ? `${employee.total_hours.toFixed(1)}h` : '0h'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                                            {employee.days_worked || 0}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{ fontSize: '14px' }}>
                                                            {employee.average_hours_per_day ? `${employee.average_hours_per_day.toFixed(1)}h` : '0h'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.overtime_hours > 0 ? '#f59e0b' : '#6b7280'
                                                        }}>
                                                            {employee.overtime_hours ? `${employee.overtime_hours.toFixed(1)}h` : '0h'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: employee.attendance_rate >= 90 ? '#059669' : employee.attendance_rate >= 75 ? '#f59e0b' : '#dc2626'
                                                        }}>
                                                            {employee.attendance_rate ? `${employee.attendance_rate.toFixed(1)}%` : '0%'}
                                                        </span>
                                                </td>
                                                <td>
                                                        <span
                                                            className="status-badge"
                                                            style={{
                                                                backgroundColor: employee.attendance_rate >= 90 ? '#059669' + '20' : employee.attendance_rate >= 75 ? '#f59e0b' + '20' : '#dc2626' + '20',
                                                                color: employee.attendance_rate >= 90 ? '#059669' : employee.attendance_rate >= 75 ? '#f59e0b' : '#dc2626',
                                                                border: `1px solid ${employee.attendance_rate >= 90 ? '#059669' : employee.attendance_rate >= 75 ? '#f59e0b' : '#dc2626'}40`
                                                            }}
                                                        >
                                                            {employee.attendance_rate >= 90 ? 'Excellent' : employee.attendance_rate >= 75 ? 'Bon' : 'Faible'}
                                                        </span>
                                                </td>
                                                <td>
                                                    <div className="actions-cell">
                                                        <button
                                                            className="action-icon"
                                                            title="Voir détails"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployee(employee);
                                                                setViewMode('employee-detail');
                                                            }}
                                                        >
                                                            <Eye style={{ width: '16px', height: '16px' }} />
                                                        </button>
                                                        <button className="action-icon" title="Modifier">
                                                            <Edit style={{ width: '16px', height: '16px' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}

                                {!isLoading && monthlyData.length === 0 && (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#64748b'
                                    }}>
                                        <CalendarIcon style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
                                        <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            Aucune donnée mensuelle trouvée
                                        </p>
                                        <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                                            Synchronisez avec le dispositif ZK pour récupérer les données.
                                        </p>
                                        <button
                                            className="action-button"
                                            onClick={syncWithZKDevice}
                                            disabled={isSyncing}
                                        >
                                            <Cable style={{ width: '16px', height: '16px' }} />
                                            Synchroniser maintenant
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        // EMPLOYEE DETAIL VIEW - Complete implementation
                        <>
                            <div style={{ marginBottom: '20px' }}>
                                <button
                                    onClick={() => setViewMode(selectedPeriod.includes('week') ? 'weekly' : 'monthly')}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #d1d5db',
                                        background: 'white',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <ArrowLeft style={{ width: '16px', height: '16px' }} />
                                    Retour à la liste
                                </button>

                                <div className="page-header">
                                    <h1 className="page-title">
                                        Détails - {selectedEmployee ? selectedEmployee.name : ''}
                                    </h1>
                                    <p className="page-subtitle">
                                        Heures de travail détaillées jour par jour
                                    </p>

                                    <div className="page-actions">
                                        <select
                                            value={selectedPeriod}
                                            onChange={(e) => setSelectedPeriod(e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="current_week">Semaine courante</option>
                                            <option value="current_month">Mois courant</option>
                                            <option value="last_week">Semaine dernière</option>
                                            <option value="last_month">Mois dernier</option>
                                        </select>
                                        <button
                                            className="action-button secondary"
                                            onClick={() => exportReport('employee', selectedEmployee)}
                                        >
                                            <Download style={{ width: '16px', height: '16px' }} />
                                            Exporter
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Employee Summary Cards */}
                            {selectedEmployee && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '30px' }}>
                                    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <Timer style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>HEURES TOTALES</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1e40af' }}>
                                            {selectedEmployee.total_hours ? `${selectedEmployee.total_hours.toFixed(1)}h` : '0h'}
                                        </p>
                                    </div>

                                    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <CheckCircle style={{ width: '20px', height: '20px', color: '#059669' }} />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>JOURS TRAVAILLÉS</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                                            {selectedEmployee.days_worked || 0}
                                        </p>
                                    </div>

                                    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <BarChart style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>MOYENNE/JOUR</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                                            {selectedEmployee.average_hours_per_day ? `${selectedEmployee.average_hours_per_day.toFixed(1)}h` : '0h'}
                                        </p>
                                    </div>

                                    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <Percent style={{ width: '20px', height: '20px', color: '#dc2626' }} />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>TAUX PRÉSENCE</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>
                                            {selectedEmployee.attendance_rate ? `${selectedEmployee.attendance_rate.toFixed(1)}%` : '0%'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Daily Details Table */}
                            <div className="products-table-section">
                                <div className="table-header">
                                    <h2 className="table-title">Détails Journaliers</h2>
                                    <span className="table-stats">
                                        {employeeDetails.length} jour{employeeDetails.length > 1 ? 's' : ''}
                                    </span>
                                </div>

                                {isLoading ? (
                                    <div style={{ padding: '40px', textAlign: 'center' }}>
                                        <RefreshCw style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                                        <p style={{ marginTop: '16px' }}>Chargement des détails...</p>
                                    </div>
                                ) : (
                                    <table className="products-table">
                                        <thead className="table-head">
                                        <tr>
                                            <th>Date</th>
                                            <th>Jour</th>
                                            <th>Arrivée</th>
                                            <th>Sortie</th>
                                            <th>Heures Travaillées</th>
                                            <th>Pause</th>
                                            <th>Heures Sup</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody className="table-body">
                                        {employeeDetails.map((record, index) => {
                                            const status = getStatusBadge(record.status);
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <div>
                                                            <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                                                {formatDate(record.date)}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                            <span style={{ fontSize: '14px', color: '#6b7280' }}>
                                                                {record.day_of_week || new Date(record.date).toLocaleDateString('fr-FR', { weekday: 'long' })}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                                                {formatTime(record.check_in)}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                                                {formatTime(record.check_out)}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{
                                                                fontSize: '16px',
                                                                fontWeight: '600',
                                                                color: record.hours > 0 ? '#1e40af' : '#6b7280'
                                                            }}>
                                                                {record.hours ? `${record.hours.toFixed(1)}h` : '0h'}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{ fontSize: '14px', color: '#6b7280' }}>
                                                                {record.break_time ? `${record.break_time}min` : '-'}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                color: record.overtime > 0 ? '#f59e0b' : '#6b7280'
                                                            }}>
                                                                {record.overtime ? `${record.overtime.toFixed(1)}h` : '0h'}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span
                                                                className="status-badge"
                                                                style={{
                                                                    backgroundColor: status.color + '20',
                                                                    color: status.color,
                                                                    border: `1px solid ${status.color}40`
                                                                }}
                                                            >
                                                                {status.text}
                                                            </span>
                                                    </td>
                                                    <td>
                                                        <div className="actions-cell">
                                                            <button className="action-icon" title="Modifier">
                                                                <Edit style={{ width: '16px', height: '16px' }} />
                                                            </button>
                                                            <button className="action-icon" title="Détails">
                                                                <Eye style={{ width: '16px', height: '16px' }} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                )}

                                {!isLoading && employeeDetails.length === 0 && (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#64748b'
                                    }}>
                                        <CalendarIcon style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
                                        <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            Aucun détail trouvé
                                        </p>
                                        <p style={{ fontSize: '14px' }}>
                                            Aucune donnée détaillée disponible pour cette période.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Notifications Container */}
            {notifications.length > 0 && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999,
                    pointerEvents: 'none'
                }}>
                    <div style={{ pointerEvents: 'auto' }}>
                        {notifications.map(notification => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onRemove={removeNotification}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* CSS Styles */}
            <style>
                {`
                    @keyframes slideInRight {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                    
                    .status-badge {
                        padding: 4px 8px;
                        border-radius: 6px;
                        font-size: 12px;
                        font-weight: 500;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    
                    .action-button {
                        padding: 8px 16px;
                        border: 1px solid #d1d5db;
                        background: white;
                        border-radius: 8px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        transition: all 0.2s ease;
                    }
                    
                    .action-button:hover {
                        background-color: #f3f4f6;
                        border-color: #9ca3af;
                    }
                    
                    .action-button:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    
                    .action-button.secondary {
                        background-color: #f8fafc;
                        border-color: #e2e8f0;
                        color: #475569;
                    }
                    
                    .action-icon {
                        padding: 6px;
                        border: none;
                        background: transparent;
                        border-radius: 4px;
                        cursor: pointer;
                        color: #6b7280;
                        transition: all 0.2s ease;
                    }
                    
                    .action-icon:hover {
                        background-color: #f3f4f6;
                        color: #374151;
                    }
                    
                    .actions-cell {
                        display: flex;
                        gap: 4px;
                        justify-content: center;
                    }
                    
                    .page-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: start;
                        margin-bottom: 24px;
                    }
                    
                    .page-title {
                        font-size: 28px;
                        font-weight: 700;
                        color: #1f2937;
                        margin: 0 0 8px 0;
                    }
                    
                    .page-subtitle {
                        font-size: 16px;
                        color: #6b7280;
                        margin: 0;
                    }
                    
                    .page-actions {
                        display: flex;
                        gap: 12px;
                        align-items: center;
                        flex-wrap: wrap;
                    }
                    
                    .table-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 16px;
                    }
                    
                    .table-title {
                        font-size: 18px;
                        font-weight: 600;
                        color: #1f2937;
                        margin: 0;
                    }
                    
                    .table-stats {
                        font-size: 14px;
                        color: #6b7280;
                    }
                    
                    .notification-badge {
                        position: absolute;
                        top: -2px;
                        right: -2px;
                        background: #dc2626;
                        color: white;
                        border-radius: 50%;
                        width: 16px;
                        height: 16px;
                        font-size: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 600;
                    }
                    
                    .header-button {
                        position: relative;
                        padding: 8px;
                        border: none;
                        background: transparent;
                        border-radius: 6px;
                        cursor: pointer;
                        color: #6b7280;
                        transition: all 0.2s ease;
                    }
                    
                    .header-button:hover {
                        background-color: #f3f4f6;
                        color: #374151;
                    }
                    
                    .header-button:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    
                    .search-container {
                        position: relative;
                        display: flex;
                        align-items: center;
                    }
                    
                    .search-input {
                        padding: 8px 12px 8px 36px;
                        border: 1px solid #d1d5db;
                        border-radius: 6px;
                        font-size: 14px;
                        width: 200px;
                        background: white;
                        outline: none;
                    }
                    
                    .search-input:focus {
                        border-color: #1e40af;
                        box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
                    }
                    
                    .search-icon {
                        position: absolute;
                        left: 12px;
                        color: #9ca3af;
                    }
                    
                    .time-display {
                        padding: 8px 12px;
                        background: #f3f4f6;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        min-width: 60px;
                        text-align: center;
                    }
                    
                    .quick-action-card {
                        border: none;
                        outline: none;
                    }
                    
                    .quick-action-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        border-color: #1e40af !important;
                    }
                    
                    .quick-action-card:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    
                    .quick-action-card:disabled:hover {
                        transform: none;
                        box-shadow: none;
                    }
                    
                    /* Responsive Design */
                    @media (max-width: 768px) {
                        .page-header {
                            flex-direction: column;
                            gap: 16px;
                            align-items: start;
                        }
                        
                        .page-actions {
                            width: 100%;
                            justify-content: start;
                        }
                        
                        .search-input {
                            width: 150px;
                        }
                        
                        .table-header {
                            flex-direction: column;
                            gap: 8px;
                            align-items: start;
                        }
                    }
                    
                    /* Table Responsiveness */
                    .products-table {
                        min-width: 100%;
                        overflow-x: auto;
                    }
                    
                    .products-table th,
                    .products-table td {
                        white-space: nowrap;
                        padding: 12px 8px;
                    }
                    
                    .products-table th:first-child,
                    .products-table td:first-child {
                        position: sticky;
                        left: 0;
                        background: white;
                        z-index: 1;
                    }
                    
                    .products-table thead th:first-child {
                        background: #f8fafc;
                    }
                    
                    /* Loading Animation */
                    .loading-spinner {
                        animation: spin 1s linear infinite;
                    }
                    
                    /* Success States */
                    .sync-success {
                        background-color: #059669 !important;
                        color: white !important;
                    }
                    
                    .sync-loading {
                        background-color: #f59e0b !important;
                        color: white !important;
                    }
                    
                    /* Hover Effects */
                    .products-table tbody tr:hover {
                        background-color: #f8fafc;
                    }
                    
                    .nav-item:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }
                    
                    /* Focus States */
                    .action-button:focus,
                    .header-button:focus,
                    .action-icon:focus {
                        outline: 2px solid #1e40af;
                        outline-offset: 2px;
                    }
                    
                    /* Notification Styles */
                    .notification-container {
                        pointer-events: none;
                    }
                    
                    .notification-item {
                        pointer-events: auto;
                        margin-bottom: 8px;
                    }
                    
                    /* Status Color Variations */
                    .status-excellent { background: rgba(5, 150, 105, 0.1); color: #059669; border-color: rgba(5, 150, 105, 0.3); }
                    .status-good { background: rgba(245, 158, 11, 0.1); color: #f59e0b; border-color: rgba(245, 158, 11, 0.3); }
                    .status-poor { background: rgba(220, 38, 38, 0.1); color: #dc2626; border-color: rgba(220, 38, 38, 0.3); }
                    .status-present { background: rgba(5, 150, 105, 0.1); color: #059669; border-color: rgba(5, 150, 105, 0.3); }
                    .status-absent { background: rgba(220, 38, 38, 0.1); color: #dc2626; border-color: rgba(220, 38, 38, 0.3); }
                    .status-incomplete { background: rgba(245, 158, 11, 0.1); color: #f59e0b; border-color: rgba(245, 158, 11, 0.3); }
                    .status-late { background: rgba(245, 158, 11, 0.1); color: #f59e0b; border-color: rgba(245, 158, 11, 0.3); }
                `}
            </style>
        </div>
    );
};

export default IntegratedTeamManagement;